import { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';

import { create } from 'zustand';

import {
  CompanyTypesQuery,
  CountriesQuery,
  StaticDataQuery,
  requestsClient,
} from '@/shared/api';

interface IDataStore<T> {
  data: T;
  setData: (data: T) => void;
}

export const useStaticDataStore = create<
  IDataStore<StaticDataQuery['staticData']>
>((set) => ({
  data: { companySourcesOfFunds: [], companySourcesOfWealth: [] },
  setData: (data) => {
    set({ data });
  },
}));

export const useCountriesStore = create<
  IDataStore<CountriesQuery['countries']>
>((set) => ({
  data: [],
  setData: (data) => {
    set({ data });
  },
}));
export const useCompanyTypesStore = create<
  IDataStore<CompanyTypesQuery['companyTypes']['nodes']>
>((set) => ({
  data: [],
  setData: (data) => {
    set({ data });
  },
}));

export const useStaticDataController = () => {
  const setData = useStaticDataStore((state) => state.setData);
  const setCountriesData = useCountriesStore((state) => state.setData);
  const setCompanyTypes = useCompanyTypesStore((state) => state.setData);

  const { data: staticData, isLoading: isStaticDataLoading } = useQuery({
    queryKey: ['static-data'],
    queryFn: async () => await requestsClient.staticData(),
  });

  const { data: countries, isLoading: isCountriesLoading } = useQuery({
    queryKey: ['static-countries'],
    queryFn: async () => await requestsClient.countries(),
  });

  const { data: companyTypes, isLoading: isCompanyTypesLoading } = useQuery({
    queryKey: ['static-company-types'],
    queryFn: async () => await requestsClient.companyTypes(),
  });

  useEffect(() => {
    if (!staticData) return;

    setData(staticData.staticData);
  }, [staticData]);

  useEffect(() => {
    if (!countries) return;

    setCountriesData(countries.countries);
  }, [countries]);

  useEffect(() => {
    if (!companyTypes) return;

    setCompanyTypes(companyTypes.companyTypes?.nodes);
  }, [companyTypes]);

  const isLoading =
    isStaticDataLoading || isCountriesLoading || isCompanyTypesLoading;
  return isLoading;
};
